<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4">
        <h2 class="font-weight-bolder">{{ $t('dashboardSection.greenCoffee') }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class="mr-2" @click="openCreateModal">
          {{ $t('memberDataSection.greenCoffee.processGreenCoffee') }}
        </v-btn>
      </div>

      <v-card elevation="0" class="px-5">
        <v-card-text>
          <div v-if="!greenCoffeeData.length && !loading" class="text-center" style="padding-top:5%">
            <v-row align="center" justify="center">
              <v-col>
                <v-img max-height="190" max-width="190" src="/icons/nodata.svg" class="img-center"></v-img>
                <h5 class="mt-4 red--text">{{ $t("greenBeansData.notFound") }}</h5>
                <p class="text-caption black--text">
                  {{ $t('dashboardSection.greenCoffee') }} {{ $t("greenBeansData.notFound") }}.
                  <br />
                  {{ $t('memberDataSection.greenCoffee.processGreenCoffee') }} {{ $t('memberDataSection.greenCoffee.toAddRecord') }}
                </p>
              </v-col>
            </v-row>
          </div>

          <div v-else>
            <v-data-table
              :headers="headers"
              :items="greenCoffeeData"
              :items-per-page="options.itemsPerPage"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
              hide-default-footer
              :loading="loading"
              :options.sync="options"
              :loading-text="$t('loadingData')"
              :server-items-length="totalData"
            >
              <template v-slot:item.lotIdOutturn="{ item }">
                {{ item.lotId || '—' }}
              </template>
              <template v-slot:item.startDate="{ item }">
                {{ item.startDate || '—' }}
              </template>
              <template v-slot:item.quantity="{ item }">
                {{ item.quantity != null ? item.quantity + ' kg' : '—' }}
              </template>
              <template v-slot:item.status="{ item }">
                <v-btn small rounded min-width="160" depressed class="green-button" height="34"
                  v-if="item.status == 'Completed'">
                  {{ $t("memberDataSection.greenCoffee.complete") }}
                </v-btn>
              </template>
              <template v-slot:item.action="{ item }">
                <v-menu location="start">
                  <template v-slot:activator="props">
                    <v-btn icon v-bind="props.attrs" v-on="props.on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="viewItem(item)">
                      <v-list-item-title>{{ $t("view") }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <template v-slot:top>
                <div class="d-flex mt-5 mb-4 flex-wrap align-center">
                  <div class="ml-3" style="width: 280px;">
                    <label for="search">&nbsp;</label>
                    <v-text-field
                      v-model="search"
                      prepend-inner-icon="mdi-magnify"
                      outlined
                      dense
                      :placeholder="$t('Search')"
                      hide-details
                      @input="onSearch"
                    ></v-text-field>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="py-1 title">{{ from }} - {{ to }} {{ $t('of') }} {{ totalData }}</div>
                  <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page <= 1" @click="pageChange(false)">
                    <v-icon dark>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page >= totalPages" @click="pageChange(true)">
                    <v-icon dark>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                        <v-icon dark>mdi-cog-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('customization') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on" @click="exportTable">
                        <v-icon dark>mdi-download</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('download') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on" @click="printTable">
                        <v-icon dark>mdi-printer</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('print') }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>

      <CreateGreenCoffeeModal
        v-model="showCreateModal"
        :added-by-name="addedByName"
        @close="showCreateModal = false"
        @submitted="onCreateSubmitted"
      />

      <ViewGreenCoffeeModal
        v-model="showViewModal"
        :item-id="viewItemId"
        @close="showViewModal = false"
      />
    </v-container>
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import moment from "moment";
import GreenCoffeeService from "@/_services/GreenCoffeeService.js";
import CreateGreenCoffeeModal from "./CreateGreenCoffeeModal.vue";
import ViewGreenCoffeeModal from "./ViewGreenCoffeeModal.vue";

export default {
  name: "GreenCoffee",
  components: { CreateGreenCoffeeModal, ViewGreenCoffeeModal },
  mixins: [loadingMixin],
  async mounted() {
    await this.fetchList();
  },
  watch: {
    options: {
      handler() {
        this.fetchList();
      },
      deep: true,
    },
  },
  data() {
    return {
      search: "",
      debounceTimer: null,
      showCreateModal: false,
      showViewModal: false,
      viewItemId: null,
      greenCoffeeData: [],
      loading: false,
      options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      from: 0,
      to: 0,
      totalData: 0,
      totalPages: 1,
      addedByName: "Hopper Smith",
      headers: [
        { text: this.$t('memberDataSection.greenCoffee.lotIdOutturnNo'), align: "start", value: "lotIdOutturn", sortable: true, class: "black--text" },
        { text: this.$t('memberDataSection.greenCoffee.startDate'), align: "start", value: "startDate", sortable: true, class: "black--text" },
        { text: this.$t('quantity'), align: "start", value: "quantity", sortable: true, class: "black--text" },
        { text: this.$t("status"), align: "center", value: "status", sortable: true, class: "black--text" },
        { align: "center", value: "action", sortable: false, class: "black--text" },
      ],
      breadcrumbs: [
        { text: this.$t("dashboard"), disabled: false, to: { name: "Users" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("dashboardSection.greenCoffee"), disabled: true, href: "#" },
      ],
    };
  },
  methods: {
    openCreateModal() {
      this.showCreateModal = true;
    },
    onSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.fetchList();
      }, 400);
    },
    pageChange(forward) {
      if (forward) this.options.page++;
      else this.options.page--;
    },
    viewItem(item) {
      if (item?.id != null) {
        this.viewItemId = item.id;
        this.showViewModal = true;
      }
    },
    async fetchList() {
      this.loading = true;
      try {
        const opts = { ...this.options, search: this.search };
        const res = await GreenCoffeeService.getGreenCoffeeList(opts);
        if (res?.code === 200 && res?.data) {
          const rows = (res.data.rows || []).map((el) => ({
            ...el,
            startDate: el.startDate ? moment(el.startDate).format("DD-MMM-YYYY") : el.startDate,
          }));
          this.greenCoffeeData = rows;
          this.totalData = res.data.count || 0;
          this.totalPages = Math.ceil(this.totalData / this.options.itemsPerPage) || 1;
          this.from = this.totalData ? (this.options.page - 1) * this.options.itemsPerPage + 1 : 0;
          this.to = this.totalData ? Math.min(this.options.page * this.options.itemsPerPage, this.totalData) : 0;
        } else {
          this.greenCoffeeData = [];
          this.totalData = 0;
          this.from = 0;
          this.to = 0;
          this.totalPages = 1;
        }
      } catch (e) {
        this.greenCoffeeData = [];
        this.totalData = 0;
        this.from = 0;
        this.to = 0;
        this.totalPages = 1;
      } finally {
        this.loading = false;
      }
    },
    onCreateSubmitted() {
      this.$notify({ title: this.$t('memberDataSection.greenCoffee.registeredSuccess'), type: "success" });
      this.fetchList();
    },
    exportTable() {
      this.$notify({ title: this.$t('memberDataSection.greenCoffee.exportNotImplemented'), type: "info" });
    },
    printTable() {
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table-header span {
  text-transform: uppercase !important;
  color: black;
}
::v-deep .img-center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 40%;
}
</style>
